import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolAdminRoute from '../components/PoolAdminRoute'
import { isPoolAdmin } from '../utils/roles'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  settings: {
    track: string
    raceDate: string
  }
  races: Race[]
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  entries: Horse[]
  scratches?: string[]
  status: 'upcoming' | 'running' | 'final'
  results?: {
    winner?: string
    place?: string
    show?: string
  }
}

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

export default function HorseRacingPoolResultsPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  const navigate = useNavigate()
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<number | null>(null) // raceNumber being saved
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const userIsPoolAdmin = isPoolAdmin(user)

  useEffect(() => {
    if (poolId && userIsPoolAdmin) {
      fetchPool()
    }
  }, [poolId, userIsPoolAdmin])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      
      // Check if this is a football pool and redirect to correct route
      if (data.pool?.type === 'football') {
        navigate(`/admin/pools/football/${poolId}/results`)
        return
      }
      
      setPool(data.pool)
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const handleSetResult = async (race: Race, winner: string, place?: string, show?: string) => {
    if (!poolId || saving) return

    try {
      setSaving(race.raceNumber)
      setError(null)

      const token = await getAccessTokenSilently()
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}/races/${race.raceNumber}/result`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          winner,
          place,
          show
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || `Failed to set race result: ${response.statusText}`)
      }

      setSuccessMessage(`Result set for Race ${race.raceNumber}`)
      
      // Refresh pool data
      await fetchPool()

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 3000)

    } catch (err) {
      console.error('Error setting race result:', err)
      setError(err instanceof Error ? err.message : 'Failed to set race result')
    } finally {
      setSaving(null)
    }
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isRaceCompleted = (race: Race) => {
    return race.status === 'final' && race.winner
  }

  if (!userIsPoolAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-4xl mb-4">🚫</div>
            <p className="text-gray-600 dark:text-gray-300">You need pool administrator privileges to access this page.</p>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !pool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Error</h1>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-4xl mb-4">❌</div>
            <p className="text-gray-600 dark:text-gray-300">{error || 'Pool not found'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <PoolAdminRoute>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/pools"
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Pools
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to="/admin/pools/horse-racing"
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  Horse Racing Admin
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <Link
                  to={`/admin/pools/horse-racing/${poolId}`}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2 dark:text-gray-400 dark:hover:text-white"
                >
                  {pool.name}
                </Link>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">
                  Race Results
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
              Race Results - {pool.name}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {pool.settings.track} • {pool.settings.raceDate}
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <Link
              to={`/admin/pools/horse-racing/${poolId}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Pool
            </Link>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
              <div className="text-sm text-green-700 dark:text-green-400">
                {successMessage}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex items-center">
              <div className="text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Races */}
        <div className="space-y-6">
          {pool.races?.map((race) => (
            <div key={race.raceNumber} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Race {race.raceNumber}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {race.raceType} • {race.distance} • {formatTime(race.postTime)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isRaceCompleted(race) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Final
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Current Results */}
                {race.winner && (
                  <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="text-sm font-medium text-green-900 dark:text-green-100 mb-2">Current Results</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="font-medium text-green-700 dark:text-green-300">Win:</span> {race.winner}
                      </div>
                      {race.placeFinisher && (
                        <div>
                          <span className="font-medium text-blue-700 dark:text-blue-300">Place:</span> {race.placeFinisher}
                        </div>
                      )}
                      {race.showFinisher && (
                        <div>
                          <span className="font-medium text-yellow-700 dark:text-yellow-300">Show:</span> {race.showFinisher}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Result Entry Form */}
                <RaceResultForm
                  race={race}
                  onSetResult={handleSetResult}
                  saving={saving === race.raceNumber}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PoolAdminRoute>
  )
}

// Component for setting race results
interface RaceResultFormProps {
  race: Race
  onSetResult: (race: Race, winner: string, place?: string, show?: string) => void
  saving: boolean
}

function RaceResultForm({ race, onSetResult, saving }: RaceResultFormProps) {
  const [winner, setWinner] = useState(race.winner || '')
  const [place, setPlace] = useState(race.placeFinisher || '')
  const [show, setShow] = useState(race.showFinisher || '')

  // Update form state when race data changes
  useEffect(() => {
    setWinner(race.winner || '')
    setPlace(race.placeFinisher || '')
    setShow(race.showFinisher || '')
  }, [race.winner, race.placeFinisher, race.showFinisher])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!winner.trim()) return
    
    onSetResult(race, winner.trim(), place.trim() || undefined, show.trim() || undefined)
  }

  const availableHorses = race.entries.filter(horse => 
    !race.scratches?.includes(horse.horse_id)
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Winner */}
        <div>
          <label htmlFor={`winner-${race.raceNumber}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Winner *
          </label>
          <select
            id={`winner-${race.raceNumber}`}
            value={winner}
            onChange={(e) => setWinner(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
            required
          >
            <option value="">Select winner...</option>
            {availableHorses.map((horse) => (
              <option key={horse.horse_id} value={horse.horse_name}>
                #{horse.post_position} {horse.horse_name}
              </option>
            ))}
          </select>
        </div>

        {/* Place */}
        <div>
          <label htmlFor={`place-${race.raceNumber}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Place
          </label>
          <select
            id={`place-${race.raceNumber}`}
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select place...</option>
            {availableHorses.filter(horse => horse.horse_name !== winner).map((horse) => (
              <option key={horse.horse_id} value={horse.horse_name}>
                #{horse.post_position} {horse.horse_name}
              </option>
            ))}
          </select>
        </div>

        {/* Show */}
        <div>
          <label htmlFor={`show-${race.raceNumber}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Show
          </label>
          <select
            id={`show-${race.raceNumber}`}
            value={show}
            onChange={(e) => setShow(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select show...</option>
            {availableHorses.filter(horse => 
              horse.horse_name !== winner && horse.horse_name !== place
            ).map((horse) => (
              <option key={horse.horse_id} value={horse.horse_name}>
                #{horse.post_position} {horse.horse_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving || !winner.trim()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Setting Result...
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              Set Result
            </>
          )}
        </button>
      </div>
    </form>
  )
}
